/**
 * Dialog content wrapper that mirrors `shadcn/dialog.tsx` MINUS the
 * built-in top-right ✕ button. Used by panel-style modals
 * (PowerUserModal, MeetCrewModal, XaesAgentModal) that render their own
 * close affordance inside the panel's title bar for legacy fidelity.
 *
 * Rendering shadcn's `DialogContent` next to a custom ✕ produces two
 * close buttons (one absolute-positioned by shadcn, one in the bar);
 * this wrapper drops the absolute one. The shadcn primitive in
 * `src/components/ui/dialog.tsx` is left untouched per CLAUDE.md.
 *
 * `overlayClassName` lets a caller darken/lighten the backdrop for a
 * specific modal without bumping the global token (e.g. HawModal asks
 * for a near-opaque wash to match legacy `_legacy/wd.html` line 827
 * which used `background: var(--bg-panel); backdrop-filter: blur(6px)`).
 */

import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
} from "react";
import {
  Content as RadixDialogContent,
  Overlay as RadixDialogOverlay,
  Portal as RadixDialogPortal,
} from "@radix-ui/react-dialog";
import { OS_SCALE_ROOT_ID } from "@/lib/os-scale";
import { cn } from "@/lib/utils";

type PanelDialogContentProps = ComponentPropsWithoutRef<typeof RadixDialogContent> & {
  /** Extra classes applied on top of the default backdrop wash. */
  overlayClassName?: string;
};

/** Prefer `#os-scale-root` so fixed dialogs share Windows DPI compensation. */
function osScalePortalContainer(): HTMLElement | undefined {
  if (typeof document === "undefined") return undefined;
  return document.getElementById(OS_SCALE_ROOT_ID) ?? undefined;
}

export const PanelDialogContent = forwardRef<
  ElementRef<typeof RadixDialogContent>,
  PanelDialogContentProps
>(({ className, overlayClassName, children, style, ...props }, ref) => (
  <RadixDialogPortal container={osScalePortalContainer()}>
    <RadixDialogOverlay
      className={cn(
        // Legacy `_legacy/wd.html` overlays use `backdrop-filter:
        // blur(8-10px)` so the underlying dashboard reads as defocused
        // glass. Radix's default overlay is just a black tint — bake
        // the blur into the className (rather than an inline style) so
        // twMerge lets per-modal `overlayClassName` overrides win when
        // they need a different value (e.g. HawModal asks for 6 px to
        // match `_legacy/wd.html` line 827 exactly).
        "fixed inset-0 z-50 bg-black/80 backdrop-blur-[8px]",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=open]:duration-[220ms] data-[state=closed]:duration-[160ms]",
        "ease-[cubic-bezier(0.16,1,0.3,1)]",
        overlayClassName,
      )}
    />
    <RadixDialogContent
      ref={ref}
      className={cn(
        // Flex column so `.modal-shell` can shrink; overflow-y so a
        // too-tall panel stays reachable instead of clipping off-screen.
        "fixed top-[50%] left-[50%] z-50 flex w-full flex-col overflow-y-auto translate-x-[-50%] translate-y-[-50%]",
        "max-w-lg",
        // Open: fade + slight lift + scale settle. Close: faster reverse
        // so dismiss reads as intentional collapse, not a hard cut.
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=open]:zoom-in-[0.97] data-[state=closed]:zoom-out-[0.97]",
        "data-[state=open]:slide-in-from-bottom-2 data-[state=closed]:slide-out-to-bottom-1",
        "data-[state=open]:duration-[220ms] data-[state=closed]:duration-[160ms]",
        "ease-[cubic-bezier(0.16,1,0.3,1)]",
        className,
        // After `className` so the OS-compensated ceiling always wins
        // over per-modal max-h utilities. Uses --os-vh (not raw dvh)
        // because dialogs portal into the scaled `#os-scale-root`.
        "max-h-[calc(var(--os-vh)*100-2rem)]",
      )}
      style={style}
      {...props}
    >
      {children}
    </RadixDialogContent>
  </RadixDialogPortal>
));
PanelDialogContent.displayName = "PanelDialogContent";
